"use client";

import Button from "@/components/Button";
import CustomImage from "@/components/CustomImage";
import Text from "@/components/Text";
import { transformText } from "@/utils/transformText";
import { AnimatePresence, motion } from "framer-motion";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { useState } from "react";

const DynamicSparkles = dynamic(() => import("@/components/Sparkles"), {
  ssr: false,
});

export type PathTargetType =
  | "healer"
  | "enlightenment"
  | "medium"
  | "creator"
  | "warrior";

export type PathType = {
  id: PathTargetType;
  text: string;
  image: {
    alt: string;
    inActiveSrc: string;
    src: string;
  };
};

type ButtonType = {
  text: string;
  link: string;
};

export type PathContentType = {
  target: string;
  image: {
    src: {
      desktop: string;
      tablet: string;
      mobile: string;
    };
    alt: string;
  };
  content: {
    title: string;
    body: string;
    button: ButtonType;
  };
};

type ChoosePathProps = {
  isPathPage?: boolean;
  path?: PathTargetType;
  paths: PathType[];
  content: PathContentType[];
};

const ChoosePath = ({ paths, content, isPathPage, path }: ChoosePathProps) => {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<PathTargetType>(
    isPathPage ? path! : "healer"
  );

  return (
    <AnimatePresence key="choose-path">
      <div className="choose-path">
        <div className="choose-path__paths">
          <ul className="choose-path__list">
            {!isPathPage &&
              paths.map((path) => (
                <li key={path.id} className="choose-path__item">
                  <button
                    onClick={() =>
                      isPathPage
                        ? router.push(`/paths/${path.id}`)
                        : setActiveTab(path.id)
                    }
                    className={`choose-path__button ${
                      activeTab === path.id ? "choose-path__button--active" : ""
                    }`.trim()}
                  >
                    <span className="choose-path__button-image">
                      <CustomImage
                        width="68"
                        height="68"
                        src={
                          path.id === activeTab
                            ? path.image.src
                            : path.image.inActiveSrc
                        }
                        srcTablet={
                          path.id === activeTab
                            ? path.image.src
                            : path.image.inActiveSrc
                        }
                        srcMobile={
                          path.id === activeTab
                            ? path.image.src
                            : path.image.inActiveSrc
                        }
                        alt={path.image.alt}
                      />
                      <DynamicSparkles />
                    </span>
                    <span>
                      {transformText(path.text, {
                        span: (chunk) => (
                          <span
                            key={chunk?.toString()}
                            className="choose-path__chunk"
                          >
                            {chunk}
                          </span>
                        ),
                      })}
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        </div>

        {content.map((item) =>
          item.target === activeTab ? (
            <div key={item.target} className="choose-path__content">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  type: "smooth",
                  duration: 1,
                }}
                className="choose-path__image-holder"
              >
                <CustomImage
                  isFull={false}
                  className="choose-path__image"
                  src={item.image.src.desktop}
                  srcTablet={item.image.src.tablet}
                  srcMobile={item.image.src.mobile}
                  alt={item.image.alt}
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="choose-path__body"
              >
                <h3 className="choose-path__title">{item.content.title}</h3>
                <Text
                  element="p"
                  elementStyle="subtitle"
                  className="choose-path__text"
                >
                  {item.content.body}
                </Text>
                {!isPathPage ? (
                  <Button
                    type="primary"
                    isLink
                    isTargetBlank
                    href={item.content.button.link}
                  >
                    {item.content.button.text}
                  </Button>
                ) : null}
              </motion.div>
            </div>
          ) : null
        )}
      </div>
    </AnimatePresence>
  );
};

export default ChoosePath;
